import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Badge, IconButton,
  Drawer, Divider, Chip, TextField, CircularProgress, Avatar, Collapse,
} from '@mui/material';
import {
  ShoppingCart, Add, Remove, Close, Check, TableRestaurant,
  Search, Send, Restaurant as RestaurantIcon, DinnerDining,
  WarningAmber,
} from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────
interface MenuItem {
  id: string;
  name: string;
  shortcutName?: string;
  price: number;
  description?: string;
  imageUrl?: string;
  isAvailable: boolean;
  menuCategoryId: string;
  // Optional size options (e.g. S/M/L), each with its own price.
  sizes?: { name: string; price: number }[];
}
interface MenuCategory { id: string; name: string }
interface Table { id: string; tableNumber: string; status: string; capacity: number }
interface CartItem { menuItem: MenuItem; quantity: number; specialInstructions: string; selectedSize?: { name: string; price: number } }
type ActiveTab = 'table' | 'menu';

// A cart line is uniquely identified by item + chosen size.
const cartLineKey = (c: CartItem) => `${c.menuItem.id}|${c.selectedSize?.name ?? ''}`;
// The chosen size travels to the kitchen/bill as a "Size: X" prefix in the order
// item's special instructions (no dedicated order column).
const buildSizeInstructions = (selectedSize?: { name: string }, note?: string): string => {
  const n = (note || '').trim();
  if (selectedSize) return n ? `Size: ${selectedSize.name} | ${n}` : `Size: ${selectedSize.name}`;
  return n;
};

// ─── Constants ────────────────────────────────────────────────────────────────
const API_BASE   = process.env.REACT_APP_API_URL || 'http://localhost:5134/api';
const IMAGE_BASE = API_BASE.replace('/api', '');

// Design tokens
const C = {
  bg:       '#0f172a',
  card:     '#1e293b',
  cardHov:  '#263347',
  border:   'rgba(255,255,255,0.08)',
  text:     '#e2e8f0',
  muted:    '#94a3b8',
  accent:   '#6366F1',
  accentHi: '#818cf8',
  green:    '#22c55e',
  red:      '#ef4444',
  amber:    '#f59e0b',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const statusColor = (s: string) =>
  s === 'Available' ? C.green : s === 'Occupied' ? C.amber : C.accent;
const statusBg = (s: string) =>
  s === 'Available' ? `${C.green}18` : s === 'Occupied' ? `${C.amber}18` : `${C.accent}18`;

// Touch-friendly icon button with minimum 44×44 tap target
const TapBtn: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  bg?: string;
  color?: string;
  size?: number;
  disabled?: boolean;
}> = ({ onClick, children, bg = 'transparent', color = C.muted, size = 36, disabled }) => (
  <Box
    component="button"
    onClick={disabled ? undefined : onClick}
    sx={{
      width: Math.max(size, 44), height: Math.max(size, 44),
      minWidth: 44, minHeight: 44,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: bg, color, border: 'none', cursor: disabled ? 'default' : 'pointer',
      borderRadius: '10px', opacity: disabled ? 0.5 : 1,
      WebkitTapHighlightColor: 'transparent',
      transition: 'opacity 0.15s, background 0.15s',
      '&:active': { opacity: disabled ? 0.5 : 0.7 },
    }}
  >
    {children}
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const CaptainApp: React.FC = () => {
  const { currentRestaurant } = useAuth();
  const restaurantId = currentRestaurant?.id;

  const [activeTab,      setActiveTab]      = useState<ActiveTab>('table');
  const [tables,         setTables]         = useState<Table[]>([]);
  const [tablesLoading,  setTablesLoading]  = useState(false);
  const [activeOrders,   setActiveOrders]   = useState<any[]>([]);
  const [selectedTable,  setSelectedTable]  = useState<Table | null>(null);
  const [categories,     setCategories]     = useState<MenuCategory[]>([]);
  const [items,          setItems]          = useState<MenuItem[]>([]);
  const [selectedCat,    setSelectedCat]    = useState('');
  const [menuLoading,    setMenuLoading]    = useState(false);
  const [search,         setSearch]         = useState('');
  const [cart,              setCart]              = useState<CartItem[]>([]);
  const [cartOpen,          setCartOpen]          = useState(false);
  const [orderPlaced,       setOrderPlaced]       = useState(false);
  const [placing,           setPlacing]           = useState(false);
  const [tablePickerOpen,   setTablePickerOpen]   = useState(false);  // inline table picker in cart
  const [sizePickerItem,    setSizePickerItem]    = useState<MenuItem | null>(null); // item awaiting size choice
  const [tempTableDialogOpen, setTempTableDialogOpen] = useState(false);
  const [tempTableNumber,     setTempTableNumber]     = useState('');
  const [tempTableCapacity,   setTempTableCapacity]   = useState('2');
  const [tempTableCreating,   setTempTableCreating]   = useState(false);

  // Category scroll ref for imperative scroll-to-active
  const catRef = useRef<HTMLDivElement>(null);

  // ── Load tables and active orders ────────────────────────────────────────
  const loadTablesAndOrders = useCallback(async () => {
    if (!restaurantId) return;
    setTablesLoading(true);
    try {
      const [tablesRes, ordersRes] = await Promise.all([
        apiService.get(`/table/restaurant/${restaurantId}/active`),
        apiService.get(`/orders/restaurant/${restaurantId}/active`)
      ]);
      setTables((tablesRes.data as Table[]) ?? []);
      setActiveOrders((ordersRes.data as any[]) ?? []);
    } catch (err) {
      console.error("Failed to load tables/orders:", err);
    } finally {
      setTablesLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => {
    loadTablesAndOrders();
  }, [loadTablesAndOrders]);

  // ── Load menu ────────────────────────────────────────────────────────────
  const loadMenu = useCallback(async () => {
    if (!restaurantId) return;
    setMenuLoading(true);
    try {
      const [cats, menuItems] = await Promise.all([
        apiService.getMenuCategories(restaurantId),
        apiService.getMenuItems(undefined, restaurantId),
      ]);
      setCategories(cats);
      setSelectedCat(cats[0]?.id ?? '');
      setItems(menuItems.map((i: any) => ({
        id: i.id, name: i.name, price: i.price,
        description: i.description, imageUrl: i.imageUrl,
        isAvailable: i.isAvailable, menuCategoryId: i.categoryId,
        sizes: Array.isArray(i.sizes) ? i.sizes : [],
      })));
    } finally {
      setMenuLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  // ── Cart helpers ─────────────────────────────────────────────────────────
  // Add to cart. Items with size options open the picker first; others add directly.
  const addToCart = (item: MenuItem) => {
    if (item.sizes && item.sizes.length > 0) {
      setSizePickerItem(item);
      return;
    }
    addLine(item);
  };

  // Add a concrete line (optionally with a chosen size); merges by item + size.
  const addLine = (item: MenuItem, selectedSize?: { name: string; price: number }) =>
    setCart(prev => {
      const key = cartLineKey({ menuItem: item, quantity: 1, specialInstructions: '', selectedSize });
      const ex = prev.find(c => cartLineKey(c) === key);
      if (ex) return prev.map(c => cartLineKey(c) === key ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { menuItem: item, quantity: 1, specialInstructions: '', selectedSize }];
    });

  const changeQty = (lineKey: string, delta: number) =>
    setCart(prev => prev
      .map(c => cartLineKey(c) === lineKey ? { ...c, quantity: c.quantity + delta } : c)
      .filter(c => c.quantity > 0));

  const linePrice = (c: CartItem) => c.selectedSize ? c.selectedSize.price : c.menuItem.price;
  // Total quantity of a menu item across all its size lines (for the card badge).
  const cartQty   = (id: string) => cart.filter(c => c.menuItem.id === id).reduce((s, c) => s + c.quantity, 0);
  const cartTotal = cart.reduce((s, c) => s + linePrice(c) * c.quantity, 0);
  const cartCount = cart.reduce((s, c) => s + c.quantity, 0);

  const [manualTableNumber, setManualTableNumber] = useState('');

  // ── Temp table ───────────────────────────────────────────────────────────
  const openTempTableDialog = () => {
    const existingCount = tables.filter(t => (t as any).isTemporary).length;
    setTempTableNumber(`TEMP-${existingCount + 1}`);
    setTempTableCapacity('2');
    setTempTableDialogOpen(true);
  };

  const createTempTable = async () => {
    if (!restaurantId || !tempTableNumber.trim()) return;
    setTempTableCreating(true);
    try {
      const created = await apiService.createTable({
        restaurantId,
        tableNumber: tempTableNumber.trim(),
        tableName: `Temp ${tempTableNumber.trim()}`,
        capacity: parseInt(tempTableCapacity) || 2,
        status: 1,
        isTemporary: true,
        isActive: true,
      } as any);
      setTempTableDialogOpen(false);
      await loadTablesAndOrders();
      // Auto-select the newly created temp table and jump to menu
      setSelectedTable({ id: created.id, tableNumber: created.tableNumber, status: 'Available', capacity: created.capacity });
      setActiveTab('menu');
    } catch {
      alert('Could not create temporary table. Please try again.');
    } finally {
      setTempTableCreating(false);
    }
  };

  // ── Place order ──────────────────────────────────────────────────────────
  const placeOrder = async () => {
    if (!restaurantId || cart.length === 0) return;
    setPlacing(true);
    try {
      await apiService.post('/order', {
        restaurantId,
        tableId: selectedTable?.id ?? null,
        tableCode: manualTableNumber ? manualTableNumber.trim() : undefined,
        orderType: 1, subtotal: cartTotal,
        taxAmount: 0, discountAmount: 0, totalAmount: cartTotal, source: 1,
        items: cart.map(c => ({
          menuItemId: c.menuItem.id, quantity: c.quantity,
          unitPrice: linePrice(c), totalPrice: linePrice(c) * c.quantity,
          specialInstructions: buildSizeInstructions(c.selectedSize, c.specialInstructions),
        })),
      });
      setOrderPlaced(true);
      setCart([]);
      setCartOpen(false);
      setManualTableNumber('');
      loadTablesAndOrders();
      setTimeout(() => { setOrderPlaced(false); setSelectedTable(null); setActiveTab('table'); }, 2500);
    } catch {
      alert('Failed to place order. Please check your table configuration or try again.');
    } finally {
      setPlacing(false);
    }
  };

  const filteredItems = items.filter(i => {
    const q = search.toLowerCase();
    const matchesSearch = !search || i.name.toLowerCase().includes(q) || (i.shortcutName || '').toLowerCase().includes(q);
    return i.isAvailable && (selectedCat ? i.menuCategoryId === selectedCat : true) && matchesSearch;
  });

  // Bottom padding = cart bar height + iOS safe-area
  const contentPb = cartCount > 0 ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : 'calc(16px + env(safe-area-inset-bottom, 0px))';

  // ────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Global mobile styles */}
      <style>{`
        html, body { margin: 0; padding: 0; overflow: hidden; height: 100%; background: ${C.bg}; }
        * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
        ::-webkit-scrollbar { width: 0; height: 0; }
        @keyframes ca-pop { 0%{transform:scale(0.92);opacity:0} 100%{transform:scale(1);opacity:1} }
        @keyframes ca-slide-up { 0%{transform:translateY(12px);opacity:0} 100%{transform:translateY(0);opacity:1} }
        @keyframes ca-success { 0%{transform:scale(0.8);opacity:0} 60%{transform:scale(1.1)} 100%{transform:scale(1);opacity:1} }
        @keyframes ca-pulse { 0%,100%{box-shadow:0 0 0 0 ${C.green}50} 50%{box-shadow:0 0 0 8px transparent} }
      `}</style>

      {/* ── Full-viewport shell ─────────────────────────────────────────────── */}
      <Box sx={{
        position: 'fixed', inset: 0,
        display: 'flex', flexDirection: 'column',
        background: C.bg,
        // Respect notch/status-bar on iOS
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}>

        {/* ── HEADER ─────────────────────────────────────────────────────────── */}
        <Box sx={{
          flexShrink: 0,
          background: C.card,
          borderBottom: `1px solid ${C.border}`,
          px: { xs: 2, sm: 2.5 },
          py: 1.25,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          zIndex: 10,
        }}>
          {/* Logo + title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
              background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentHi} 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 12px ${C.accent}60`,
            }}>
              <DinnerDining sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                color: C.text, fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1rem' },
                lineHeight: 1.2, letterSpacing: '-0.01em',
              }}>
                Captain App
              </Typography>
              <Typography sx={{ color: selectedTable ? C.accent : C.muted, fontSize: '0.7rem', fontWeight: 600, mt: 0.1 }} noWrap>
                {selectedTable ? `📍 Table ${selectedTable.tableNumber}` : (restaurantId ? 'Select table or browse menu' : 'No restaurant linked')}
              </Typography>
            </Box>
          </Box>

          {/* Cart button */}
          <Badge
            badgeContent={cartCount}
            color="error"
            sx={{ '& .MuiBadge-badge': { fontWeight: 800, fontSize: '0.65rem', minWidth: 18, height: 18 } }}
          >
            <Box
              component="button"
              onClick={() => cartCount > 0 && setCartOpen(true)}
              sx={{
                width: 44, height: 44, borderRadius: '12px', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: cartCount > 0 ? `${C.accent}20` : 'transparent',
                color: cartCount > 0 ? C.accent : C.muted,
                outline: cartCount > 0 ? `1.5px solid ${C.accent}60` : `1px solid ${C.border}`,
                cursor: cartCount > 0 ? 'pointer' : 'default',
                transition: 'all 0.2s',
                animation: cartCount > 0 ? 'ca-pulse 2s infinite' : 'none',
              }}
            >
              <ShoppingCart sx={{ fontSize: 20 }} />
            </Box>
          </Badge>
        </Box>

        {/* ── TABS ───────────────────────────────────────────────────────────── */}
        <Box sx={{
          flexShrink: 0,
          display: 'flex',
          background: C.card,
          borderBottom: `1px solid ${C.border}`,
          zIndex: 9,
        }}>
          {(['table', 'menu'] as ActiveTab[]).map(tab => {
            const active = activeTab === tab;
            return (
              <Box
                key={tab}
                component="button"
                onClick={() => setActiveTab(tab)}
                sx={{
                  flex: 1, py: 1.25, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 0.75,
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  borderBottom: active ? `2.5px solid ${C.accent}` : '2.5px solid transparent',
                  transition: 'border-color 0.2s',
                  minHeight: 48, // 48px minimum tap target height
                }}
              >
                {tab === 'table'
                  ? <TableRestaurant sx={{ fontSize: 18, color: active ? C.accent : C.muted, transition: 'color 0.2s' }} />
                  : <RestaurantIcon  sx={{ fontSize: 18, color: active ? C.accent : C.muted, transition: 'color 0.2s' }} />
                }
                <Typography sx={{
                  fontSize: '0.875rem', fontWeight: active ? 700 : 500,
                  color: active ? C.accent : C.muted,
                  transition: 'color 0.2s, font-weight 0.2s',
                  userSelect: 'none',
                }}>
                  {tab === 'table' ? 'Tables' : 'Menu'}
                </Typography>
                {/* Cart count badge on Menu tab */}
                {tab === 'menu' && cartCount > 0 && (
                  <Box sx={{
                    minWidth: 20, height: 20, borderRadius: '10px',
                    background: C.red, px: 0.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    animation: 'ca-pop 0.25s ease',
                  }}>
                    <Typography sx={{ color: '#fff', fontSize: '0.6rem', fontWeight: 800, lineHeight: 1 }}>
                      {cartCount}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* ── No restaurant ────────────────────────────────────────────────────── */}
        {!restaurantId && (
          <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            p: 3, gap: 2, animation: 'ca-slide-up 0.3s ease',
          }}>
            <Typography sx={{ fontSize: '3rem' }}>⚠️</Typography>
            <Typography sx={{ color: C.amber, fontWeight: 800, fontSize: '1.05rem', textAlign: 'center' }}>
              No restaurant linked
            </Typography>
            <Typography sx={{ color: C.muted, fontSize: '0.85rem', textAlign: 'center', lineHeight: 1.6, maxWidth: 280 }}>
              Ask your admin to assign a restaurant to your account.
            </Typography>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: TABLES                                                         */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {restaurantId && activeTab === 'table' && (
          <Box sx={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            px: { xs: 1.5, sm: 2 },
            pt: 2,
            pb: contentPb,
            animation: 'ca-slide-up 0.25s ease',
          }}>
            {tablesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                <CircularProgress sx={{ color: C.accent }} />
              </Box>
            ) : tables.length === 0 ? (
              <Box sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', pt: 6, gap: 2.5, px: 2,
              }}>
                <Box sx={{
                  width: 88, height: 88, borderRadius: '22px',
                  background: `${C.accent}18`, border: `1px solid ${C.accent}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <TableRestaurant sx={{ fontSize: 44, color: C.accent }} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.1rem', mb: 0.5 }}>
                    No tables configured
                  </Typography>
                  <Typography sx={{ color: C.muted, fontSize: '0.82rem', lineHeight: 1.6 }}>
                    Go to admin panel → <span style={{ color: C.accent, fontWeight: 700 }}>Tables</span> to add tables.
                  </Typography>
                </Box>
                <Box sx={{
                  background: `${C.green}12`, border: `1px solid ${C.green}35`,
                  borderRadius: '16px', px: 2.5, py: 1.5, width: '100%',
                }}>
                  <Typography sx={{ color: C.green, fontSize: '0.82rem', fontWeight: 600, textAlign: 'center', lineHeight: 1.5 }}>
                    💡 You can still browse &amp; take orders from the <strong>Menu</strong> tab
                  </Typography>
                </Box>
                <Box
                  component="button"
                  onClick={openTempTableDialog}
                  sx={{
                    width: '100%', border: `2px dashed ${C.amber}80`,
                    background: `${C.amber}10`, color: C.amber,
                    borderRadius: '14px', py: 1.5,
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem',
                    minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    '&:active': { opacity: 0.7 },
                  }}
                >
                  + Create Temporary Table
                </Box>
              </Box>
            ) : (
              <>
                {/* Selected table banner */}
                <Collapse in={!!selectedTable}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: `${C.accent}18`, border: `1.5px solid ${C.accent}50`,
                    borderRadius: '14px', px: 2, py: 1.25, mb: 2,
                    animation: 'ca-slide-up 0.2s ease',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TableRestaurant sx={{ color: C.accent, fontSize: 20 }} />
                      <Typography sx={{ color: C.accent, fontWeight: 700, fontSize: '0.9rem' }}>
                        Table {selectedTable?.tableNumber} selected
                      </Typography>
                    </Box>
                    <Box component="button" onClick={() => setSelectedTable(null)} sx={{ border: 'none', background: 'transparent', cursor: 'pointer', color: C.muted, p: 0.5 }}>
                      <Close sx={{ fontSize: 18 }} />
                    </Box>
                  </Box>
                </Collapse>

                {/* Section label + Temp Table button */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
                  <Typography sx={{
                    color: C.muted, fontSize: '0.7rem', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>
                    {tables.length} Table{tables.length !== 1 ? 's' : ''}
                  </Typography>
                  <Box
                    component="button"
                    onClick={openTempTableDialog}
                    sx={{
                      border: `1.5px dashed ${C.amber}80`,
                      background: `${C.amber}10`,
                      color: C.amber,
                      borderRadius: '10px',
                      px: 1.5, py: 0.5,
                      cursor: 'pointer',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      minHeight: 34,
                      transition: 'all 0.15s',
                      '&:active': { opacity: 0.7 },
                    }}
                  >
                    + Temp Table
                  </Box>
                </Box>

                {/* Responsive table grid */}
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: { xs: 1.5, sm: 2 },
                }}>
                  {tables.map(t => {
                    const sel = selectedTable?.id === t.id;
                    const activeOrder = activeOrders.find(o => o.tableId?.toLowerCase() === t.id.toLowerCase());
                    const isOccupied = !!activeOrder;
                    const isTemp = !!(t as any).isTemporary;
                    const statusText = isOccupied ? 'Occupied' : 'Vacant';
                    const statusColorVal = isOccupied ? C.amber : C.green;
                    const statusBgVal = isOccupied ? `${C.amber}18` : `${C.green}18`;

                    return (
                      <Card
                        key={t.id}
                        onClick={() => {
                          setSelectedTable(sel ? null : t);
                          if (!sel) setActiveTab('menu');
                        }}
                        sx={{
                          background: sel ? `${C.accent}22` : C.card,
                          border: `2px ${isTemp ? 'dashed' : 'solid'} ${sel ? C.accent : isTemp ? `${C.amber}70` : isOccupied ? `${C.amber}40` : C.border}`,
                          borderRadius: '18px', cursor: 'pointer',
                          transition: 'all 0.18s',
                          boxShadow: sel ? `0 4px 16px ${C.accent}30` : isOccupied ? `0 4px 12px ${C.amber}15` : 'none',
                          '&:active': { transform: 'scale(0.98)' },
                          '&:hover': { borderColor: C.accent, background: C.cardHov },
                          p: 2,
                          display: 'flex', flexDirection: 'column',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Box sx={{
                              width: 40, height: 40, borderRadius: '10px',
                              background: isOccupied ? `${C.amber}15` : `${C.accent}15`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <TableRestaurant sx={{ color: sel ? C.accent : isOccupied ? C.amber : C.text, fontSize: 22 }} />
                            </Box>
                            <Box sx={{ textAlign: 'left' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '0.95rem' }}>Table {t.tableNumber}</Typography>
                                {isTemp && (
                                  <Box sx={{ fontSize: '0.55rem', fontWeight: 800, color: C.amber, border: `1px dashed ${C.amber}`, borderRadius: '4px', px: 0.5, lineHeight: '16px' }}>
                                    TEMP
                                  </Box>
                                )}
                              </Box>
                              <Typography sx={{ color: C.muted, fontSize: '0.7rem' }}>{t.capacity} seats</Typography>
                            </Box>
                          </Box>
                          <Chip label={statusText} size="small" sx={{ fontSize: '0.65rem', fontWeight: 800, background: statusBgVal, color: statusColorVal }} />
                        </Box>

                        {/* Active Order Details */}
                        {isOccupied && activeOrder.orderItems && activeOrder.orderItems.length > 0 ? (
                          <Box sx={{
                            mt: 1.5,
                            pt: 1.25,
                            borderTop: `1px dashed ${C.border}`,
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                          }}>
                            <Typography sx={{ color: C.muted, fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.02em', textTransform: 'uppercase', mb: 0.25 }}>
                              Active Order: {activeOrder.orderNumber}
                            </Typography>
                            {activeOrder.orderItems.map((oi: any) => (
                              <Box key={oi.id}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ color: C.text, fontSize: '0.75rem', fontWeight: 600, display: 'flex', gap: 0.5, alignItems: 'center', minWidth: 0, flex: 1 }} noWrap>
                                    <span style={{ color: C.accent, fontWeight: 800 }}>{oi.quantity}x</span>
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{oi.menuItemName || 'Item'}</span>
                                  </Typography>
                                  <Typography sx={{ color: C.green, fontSize: '0.72rem', fontWeight: 700, flexShrink: 0 }}>
                                    ₹{oi.totalPrice.toFixed(0)}
                                  </Typography>
                                </Box>
                                {oi.specialInstructions && (
                                  <Typography sx={{ color: '#f59e0b', fontSize: '0.65rem', fontStyle: 'italic', pl: 1.5 }}>
                                    ★ {oi.specialInstructions}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                            {activeOrder.notes && (
                              <Typography sx={{ color: '#f59e0b', fontSize: '0.68rem', fontStyle: 'italic', mt: 0.5, px: 0.5, py: 0.25, borderRadius: 1, background: 'rgba(245,158,11,0.1)' }}>
                                📝 {activeOrder.notes}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75, pt: 0.75, borderTop: `1px solid rgba(255,255,255,0.06)` }}>
                              <Typography sx={{ color: C.text, fontSize: '0.75rem', fontWeight: 800 }}>
                                Total:
                              </Typography>
                              <Typography sx={{ color: C.green, fontSize: '0.8rem', fontWeight: 900 }}>
                                ₹{activeOrder.totalAmount.toFixed(0)}
                              </Typography>
                            </Box>
                          </Box>
                        ) : isOccupied ? (
                          <Box sx={{
                            mt: 1.5,
                            pt: 1.25,
                            borderTop: `1px dashed ${C.border}`,
                            textAlign: 'left',
                          }}>
                            <Typography sx={{ color: C.muted, fontSize: '0.7rem', fontStyle: 'italic' }}>
                              No items loaded for active order.
                            </Typography>
                          </Box>
                        ) : null}
                      </Card>
                    );
                  })}
                </Box>

                {!selectedTable && (
                  <Typography sx={{
                    color: C.muted, fontSize: '0.78rem', textAlign: 'center', mt: 3, lineHeight: 1.6,
                  }}>
                    Tap a table to select it, then order from the{' '}
                    <span style={{ color: C.accent, fontWeight: 700 }}>Menu</span> tab
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════════════════ */}
        {/* TAB: MENU                                                           */}
        {/* ════════════════════════════════════════════════════════════════════ */}
        {restaurantId && activeTab === 'menu' && (
          <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column',
            overflow: 'hidden', animation: 'ca-slide-up 0.25s ease',
          }}>

            {/* Table context / warning strip */}
            {selectedTable ? (
              <Box sx={{
                flexShrink: 0, px: 2, py: 1,
                background: `${C.accent}12`, borderBottom: `1px solid ${C.accent}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <TableRestaurant sx={{ color: C.accent, fontSize: 16 }} />
                  <Typography sx={{ color: C.accent, fontSize: '0.78rem', fontWeight: 700 }}>
                    Ordering for Table {selectedTable.tableNumber}
                  </Typography>
                </Box>
                <Box
                  component="button"
                  onClick={() => setActiveTab('table')}
                  sx={{
                    border: 'none', background: 'transparent', color: C.muted,
                    fontSize: '0.72rem', cursor: 'pointer', textDecoration: 'underline',
                    minHeight: 36, px: 1, py: 0.5,
                  }}
                >
                  Change
                </Box>
              </Box>
            ) : (
              <Box sx={{
                flexShrink: 0, px: 2, py: 0.875,
                background: `${C.amber}10`, borderBottom: `1px solid ${C.amber}25`,
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <WarningAmber sx={{ color: C.amber, fontSize: 16, flexShrink: 0 }} />
                <Typography sx={{ color: C.amber, fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.4 }}>
                  No table selected — order will go without table assignment
                </Typography>
              </Box>
            )}

            {/* Search bar */}
            <Box sx={{ flexShrink: 0, px: { xs: 1.5, sm: 2 }, pt: 1.25, pb: 0.5 }}>
              <TextField
                placeholder="Search menu…"
                size="small"
                fullWidth
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: C.muted, fontSize: 18, mr: 0.5 }} />,
                  endAdornment: search ? (
                    <IconButton size="small" onClick={() => setSearch('')} sx={{ color: C.muted, p: 0.5 }}>
                      <Close sx={{ fontSize: 16 }} />
                    </IconButton>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: C.card, borderRadius: '13px', minHeight: 44,
                    '& input': { color: C.text, fontSize: '0.9rem', py: '10px' },
                    '& fieldset': { borderColor: C.border },
                    '&:hover fieldset': { borderColor: C.accent },
                    '&.Mui-focused fieldset': { borderColor: C.accent },
                  },
                }}
              />
            </Box>

            {/* Category chips — horizontal scroll */}
            {!menuLoading && categories.length > 0 && (
              <Box
                ref={catRef}
                sx={{
                  flexShrink: 0,
                  display: 'flex', gap: 0.75,
                  px: { xs: 1.5, sm: 2 }, pb: 1, pt: 0.5,
                  overflowX: 'auto', overflowY: 'hidden',
                  scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {/* "All" chip */}
                <Chip
                  label="All"
                  size="small"
                  onClick={() => setSelectedCat('')}
                  sx={{
                    flexShrink: 0, fontWeight: 700, fontSize: '0.75rem',
                    height: 32, cursor: 'pointer',
                    background: !selectedCat ? C.accent : C.card,
                    color: !selectedCat ? '#fff' : C.muted,
                    border: `1.5px solid ${!selectedCat ? C.accent : C.border}`,
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: C.accent },
                  }}
                />
                {categories.map(cat => (
                  <Chip
                    key={cat.id}
                    label={cat.name}
                    size="small"
                    onClick={() => setSelectedCat(cat.id)}
                    sx={{
                      flexShrink: 0, fontWeight: 700, fontSize: '0.75rem',
                      height: 32, cursor: 'pointer',
                      background: selectedCat === cat.id ? C.accent : C.card,
                      color: selectedCat === cat.id ? '#fff' : C.muted,
                      border: `1.5px solid ${selectedCat === cat.id ? C.accent : C.border}`,
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: C.accent },
                    }}
                  />
                ))}
              </Box>
            )}

            {/* ── Item list ── scrollable zone ── */}
            <Box sx={{
              flex: 1, overflowY: 'auto', overflowX: 'hidden',
              px: { xs: 1.5, sm: 2 },
              pb: contentPb,
              WebkitOverflowScrolling: 'touch',
            }}>
              {menuLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
                  <CircularProgress sx={{ color: C.accent }} />
                </Box>
              ) : filteredItems.length === 0 ? (
                <Box sx={{ textAlign: 'center', pt: 8, animation: 'ca-slide-up 0.25s ease' }}>
                  <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>🍽️</Typography>
                  <Typography sx={{ color: C.muted, fontSize: '0.875rem' }}>
                    {search ? 'No items match your search' : 'No items in this category'}
                  </Typography>
                  {search && (
                    <Box
                      component="button"
                      onClick={() => setSearch('')}
                      sx={{
                        mt: 2, border: `1px solid ${C.border}`, background: 'transparent',
                        color: C.accent, borderRadius: '10px', px: 2, py: 0.75,
                        cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, minHeight: 44,
                      }}
                    >
                      Clear search
                    </Box>
                  )}
                </Box>
              ) : (
                filteredItems.map(item => {
                  const qty = cartQty(item.id);
                  return (
                    <Box
                      key={item.id}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25,
                        background: qty > 0 ? `${C.accent}0e` : C.card,
                        border: `1.5px solid ${qty > 0 ? `${C.accent}55` : C.border}`,
                        borderRadius: '14px', mb: 1,
                        px: { xs: 1.25, sm: 1.5 }, py: { xs: 1, sm: 1.25 },
                        transition: 'border-color 0.15s, background 0.15s',
                        animation: 'ca-slide-up 0.2s ease',
                      }}
                    >
                      {/* Thumbnail */}
                      {item.imageUrl ? (
                        <Box sx={{
                          flexShrink: 0, width: { xs: 52, sm: 60 }, height: { xs: 52, sm: 60 },
                          borderRadius: '10px', overflow: 'hidden',
                        }}>
                          <img
                            src={item.imageUrl.startsWith('http') ? item.imageUrl : `${IMAGE_BASE}${item.imageUrl}`}
                            alt={item.name}
                            loading="lazy"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        </Box>
                      ) : (
                        <Avatar sx={{
                          flexShrink: 0, width: { xs: 48, sm: 56 }, height: { xs: 48, sm: 56 },
                          borderRadius: '10px', background: `${C.accent}20`, fontSize: '1.4rem',
                        }}>
                          🍴
                        </Avatar>
                      )}

                      {/* Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          color: C.text, fontWeight: 700,
                          fontSize: { xs: '0.85rem', sm: '0.9rem' },
                          lineHeight: 1.3,
                        }} noWrap>
                          {item.name}
                        </Typography>
                        {item.description && (
                          <Typography sx={{
                            color: C.muted, fontSize: '0.7rem', mt: 0.2, display: 'block',
                          }} noWrap>
                            {item.description}
                          </Typography>
                        )}
                        <Typography sx={{ color: C.green, fontWeight: 800, mt: 0.35, fontSize: '0.9rem' }}>
                          {item.sizes && item.sizes.length > 0
                            ? `From ₹${Math.min(...item.sizes.map(s => s.price))}`
                            : `₹${item.price}`}
                        </Typography>
                        {item.sizes && item.sizes.length > 0 && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.4 }}>
                            {item.sizes.map(s => (
                              <Chip key={s.name} label={s.name} size="small"
                                sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${C.accent}20`, color: C.text }} />
                            ))}
                          </Box>
                        )}
                      </Box>

                      {/* Qty controls — 44px touch targets */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                        {qty > 0 && (
                          <>
                            {!(item.sizes && item.sizes.length > 0) && (
                              <TapBtn
                                onClick={() => changeQty(`${item.id}|`, -1)}
                                bg={`${C.red}18`} color={C.red} size={36}
                              >
                                <Remove sx={{ fontSize: 17 }} />
                              </TapBtn>
                            )}
                            <Typography sx={{
                              color: C.text, fontWeight: 900,
                              minWidth: { xs: 24, sm: 28 }, textAlign: 'center',
                              fontSize: '0.95rem',
                            }}>
                              {qty}
                            </Typography>
                          </>
                        )}
                        <TapBtn
                          onClick={() => addToCart(item)}
                          bg={qty > 0 ? C.accent : `${C.accent}25`}
                          color={qty > 0 ? '#fff' : C.accent}
                          size={36}
                        >
                          <Add sx={{ fontSize: 17 }} />
                        </TapBtn>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        )}

        {/* ── Cart bar — fixed above safe-area bottom ───────────────────────── */}
        {cartCount > 0 && (
          <Box sx={{
            flexShrink: 0,
            px: { xs: 1.5, sm: 2 },
            pt: 1,
            pb: `calc(12px + env(safe-area-inset-bottom, 0px))`,
            background: `linear-gradient(to top, ${C.bg} 75%, transparent)`,
          }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => setCartOpen(true)}
              sx={{
                background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentHi} 100%)`,
                borderRadius: '15px', minHeight: 54,
                display: 'flex', justifyContent: 'space-between',
                px: 2.5, py: 1.25,
                boxShadow: `0 6px 24px ${C.accent}55`,
                textTransform: 'none', fontWeight: 700,
                '&:active': { transform: 'scale(0.98)' },
                transition: 'transform 0.1s',
              }}
            >
              <Box sx={{
                background: 'rgba(255,255,255,0.22)', borderRadius: '9px',
                minWidth: 30, height: 28, px: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.9rem' }}>
                  {cartCount}
                </Typography>
              </Box>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem' }}>
                View Order
              </Typography>
              <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.95rem' }}>
                ₹{cartTotal.toFixed(0)}
              </Typography>
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Cart Drawer ──────────────────────────────────────────────────────── */}
      {/* Size picker — bottom sheet shown when adding an item with size options */}
      <Drawer
        anchor="bottom"
        open={!!sizePickerItem}
        onClose={() => setSizePickerItem(null)}
        PaperProps={{
          sx: {
            background: C.card, borderRadius: '22px 22px 0 0',
            maxWidth: 560, left: '50%', transform: 'translateX(-50%) !important',
            width: '100%', p: 2.5,
            pb: `calc(20px + env(safe-area-inset-bottom, 0px))`,
          },
        }}
      >
        <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1rem', mb: 0.5 }}>
          Choose size
        </Typography>
        <Typography sx={{ color: C.muted, fontSize: '0.82rem', mb: 1.5 }} noWrap>
          {sizePickerItem?.name}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {(sizePickerItem?.sizes || []).map(sz => (
            <Box
              key={sz.name}
              component="button"
              onClick={() => {
                if (sizePickerItem) addLine(sizePickerItem, { name: sz.name, price: sz.price });
                setSizePickerItem(null);
                setCartOpen(true);
              }}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                border: `1.5px solid ${C.border}`, background: `${C.accent}10`,
                borderRadius: '12px', px: 2, py: 1.5, cursor: 'pointer',
                color: C.text, fontSize: '0.95rem', fontWeight: 700, minHeight: 48,
              }}
            >
              <span>{sz.name}</span>
              <span style={{ color: C.green, fontWeight: 800 }}>₹{sz.price}</span>
            </Box>
          ))}
        </Box>
        <Box
          component="button"
          onClick={() => setSizePickerItem(null)}
          sx={{
            mt: 1.5, width: '100%', border: 'none', background: 'transparent',
            color: C.muted, fontSize: '0.85rem', cursor: 'pointer', minHeight: 40,
          }}
        >
          Cancel
        </Box>
      </Drawer>

      <Drawer
        anchor="bottom"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: {
            background: C.card, borderRadius: '22px 22px 0 0',
            maxHeight: '82vh',
            // Center on wider screens
            maxWidth: 560, left: '50%', transform: 'translateX(-50%) !important',
            width: '100%',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            pb: `env(safe-area-inset-bottom, 0px)`,
          },
        }}
      >
        {/* Drag handle */}
        <Box sx={{ pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.18)' }} />
        </Box>

        {/* Header */}
        <Box sx={{
          px: 2.5, pb: 1.25,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexShrink: 0,
        }}>
          <Box>
            <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1.05rem' }}>
              Your Order
            </Typography>
            {/* Selected table chip */}
            {selectedTable ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  background: `${C.accent}18`, border: `1px solid ${C.accent}50`,
                  borderRadius: '8px', px: 1, py: 0.3,
                }}>
                  <TableRestaurant sx={{ color: C.accent, fontSize: 13 }} />
                  <Typography sx={{ color: C.accent, fontSize: '0.72rem', fontWeight: 700 }}>
                    Table {selectedTable.tableNumber}
                  </Typography>
                </Box>
                <Box
                  component="button"
                  onClick={() => { setSelectedTable(null); setTablePickerOpen(true); }}
                  sx={{
                    border: 'none', background: 'transparent', color: C.muted,
                    fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline',
                    p: 0, minHeight: 24,
                  }}
                >
                  Change
                </Box>
              </Box>
            ) : (
              <Typography sx={{ color: C.amber, fontSize: '0.72rem', fontWeight: 500, mt: 0.3 }}>
                ⚠️ No table selected
              </Typography>
            )}
          </Box>
          <TapBtn onClick={() => { setCartOpen(false); setTablePickerOpen(false); }} bg="rgba(255,255,255,0.07)" color={C.muted} size={36}>
            <Close sx={{ fontSize: 18 }} />
          </TapBtn>
        </Box>

        <Divider sx={{ borderColor: C.border, flexShrink: 0 }} />

        {/* Items — scrollable */}
        <Box sx={{
          overflowY: 'auto', flex: 1,
          px: 2.5, pt: 1.5,
          WebkitOverflowScrolling: 'touch',
        }}>
          {cart.map(c => {
            const key = cartLineKey(c);
            return (
            <Box key={key} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              mb: 1.5, pb: 1.5, borderBottom: `1px solid ${C.border}`,
            }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: C.text, fontSize: '0.9rem', fontWeight: 700 }} noWrap>
                  {c.menuItem.name}
                  {c.selectedSize && (
                    <Chip label={c.selectedSize.name} size="small"
                      sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: `${C.accent}20`, color: C.text }} />
                  )}
                </Typography>
                <Typography sx={{ color: C.green, fontWeight: 700, fontSize: '0.82rem' }}>
                  ₹{linePrice(c).toFixed(0)} × {c.quantity}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <TapBtn onClick={() => changeQty(key, -1)} bg={`${C.red}18`} color={C.red} size={34}>
                  <Remove sx={{ fontSize: 15 }} />
                </TapBtn>
                <Typography sx={{ color: C.text, fontWeight: 900, minWidth: 26, textAlign: 'center', fontSize: '0.95rem' }}>
                  {c.quantity}
                </Typography>
                <TapBtn onClick={() => changeQty(key, 1)} bg={`${C.green}18`} color={C.green} size={34}>
                  <Add sx={{ fontSize: 15 }} />
                </TapBtn>
              </Box>
              <Typography sx={{
                color: C.text, fontWeight: 700, fontSize: '0.82rem',
                minWidth: 52, textAlign: 'right', ml: 0.5,
              }}>
                ₹{(linePrice(c) * c.quantity).toFixed(0)}
              </Typography>
            </Box>
            );
          })}
        </Box>

        <Divider sx={{ borderColor: C.border, flexShrink: 0 }} />

        {/* ── Inline Table Picker (slides in when no table selected) ── */}
        <Collapse in={tablePickerOpen} timeout={240}>
          <Box sx={{ px: 2.5, pt: 1.5, pb: 0, flexShrink: 0 }}>
            {/* Picker header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableRestaurant sx={{ color: C.accent, fontSize: 20 }} />
                <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '0.95rem' }}>
                  Select a Table
                </Typography>
              </Box>
              <Box
                component="button"
                onClick={() => setTablePickerOpen(false)}
                sx={{
                  border: 'none', background: 'transparent', color: C.muted,
                  fontSize: '0.75rem', cursor: 'pointer', minHeight: 36, px: 1,
                }}
              >
                Skip
              </Box>
            </Box>

            {/* Table grid inside drawer */}
            {tables.length === 0 ? (
              <Box sx={{
                background: `${C.amber}10`, border: `1px solid ${C.amber}30`,
                borderRadius: '12px', px: 2, py: 1.5, mb: 1.5,
              }}>
                <Typography sx={{ color: C.amber, fontSize: '0.8rem', textAlign: 'center', fontWeight: 500 }}>
                  No tables configured. Tap Skip to send without a table.
                </Typography>
              </Box>
            ) : (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1, mb: 1.5,
                maxHeight: 200, overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
              }}>
                {tables.map(t => {
                  const isSel = selectedTable?.id === t.id;
                  return (
                    <Box
                      key={t.id}
                      component="button"
                      onClick={() => {
                        setSelectedTable(t);
                        setTablePickerOpen(false);
                      }}
                      sx={{
                        border: `2px solid ${isSel ? C.accent : C.border}`,
                        background: isSel ? `${C.accent}22` : C.bg,
                        borderRadius: '12px', cursor: 'pointer',
                        py: 1.25, px: 0.5, textAlign: 'center',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', gap: 0.4,
                        transition: 'all 0.15s',
                        minHeight: 72,
                        '&:active': { transform: 'scale(0.95)' },
                      }}
                    >
                      <TableRestaurant sx={{ color: isSel ? C.accent : C.text, fontSize: 22 }} />
                      <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '0.78rem', lineHeight: 1.2 }}>
                        {t.tableNumber}
                      </Typography>
                      <Box sx={{
                        height: 6, width: 6, borderRadius: '50%',
                        background: statusColor(t.status),
                        flexShrink: 0,
                      }} />
                    </Box>
                  );
                })}
              </Box>
            )}

            <Divider sx={{ borderColor: C.border, mb: 0 }} />
          </Box>
        </Collapse>

        {/* Total + CTA */}
        <Box sx={{ px: 2.5, pt: 1.5, pb: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.75 }}>
            <Typography sx={{ color: C.muted, fontWeight: 500, fontSize: '0.9rem' }}>Total</Typography>
            <Typography sx={{ color: C.green, fontWeight: 900, fontSize: '1.2rem' }}>
              ₹{cartTotal.toFixed(0)}
            </Typography>
          </Box>

          {/* If no table selected and picker not open → prompt to select or enter manually */}
          {!selectedTable && !tablePickerOpen ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                placeholder="Or enter Table No. manually..."
                size="small"
                fullWidth
                value={manualTableNumber}
                onChange={e => setManualTableNumber(e.target.value)}
                InputProps={{
                  startAdornment: <TableRestaurant sx={{ color: C.muted, fontSize: 18, mr: 0.5 }} />,
                }}
                sx={{
                  mb: 0.5,
                  '& .MuiOutlinedInput-root': {
                    background: C.bg, borderRadius: '13px', minHeight: 44,
                    '& input': { color: C.text, fontSize: '0.85rem', py: '10px' },
                    '& fieldset': { borderColor: C.border },
                    '&:hover fieldset': { borderColor: C.accent },
                    '&.Mui-focused fieldset': { borderColor: C.accent },
                  },
                }}
              />
              <Button
                variant="contained"
                fullWidth
                startIcon={<TableRestaurant />}
                sx={{
                  background: manualTableNumber ? `linear-gradient(135deg, ${C.green} 0%, #16a34a 100%)` : `linear-gradient(135deg, ${C.accent} 0%, ${C.accentHi} 100%)`,
                  minHeight: 54, borderRadius: '15px', fontWeight: 800,
                  fontSize: '1rem', textTransform: 'none',
                  boxShadow: manualTableNumber ? `0 6px 20px ${C.green}45` : `0 6px 20px ${C.accent}45`,
                  '&:active': { transform: 'scale(0.98)' },
                  transition: 'all 0.2s',
                }}
                onClick={manualTableNumber ? placeOrder : () => setTablePickerOpen(true)}
                disabled={placing}
              >
                {placing ? 'Sending…' : manualTableNumber ? `Send to Table ${manualTableNumber} 🍳` : 'Select Table to Order'}
              </Button>
              {!manualTableNumber && (
                <Button
                  variant="text"
                  fullWidth
                  startIcon={placing ? <CircularProgress size={16} color="inherit" /> : <Send sx={{ fontSize: 17 }} />}
                  onClick={placeOrder}
                  disabled={placing}
                  sx={{
                    color: C.muted, fontSize: '0.82rem', textTransform: 'none',
                    minHeight: 40, fontWeight: 600,
                    '&:hover': { color: C.text, background: `${C.border}` },
                  }}
                >
                  Skip table & send to kitchen
                </Button>
              )}
            </Box>
          ) : (
            /* Table is selected or picker is open — show confirm button */
            <Button
              variant="contained"
              fullWidth
              startIcon={placing ? <CircularProgress size={18} color="inherit" /> : <Send />}
              onClick={placeOrder}
              disabled={placing || tablePickerOpen}
              sx={{
                background: tablePickerOpen
                  ? `${C.green}50`
                  : `linear-gradient(135deg, ${C.green} 0%, #16a34a 100%)`,
                minHeight: 54, borderRadius: '15px', fontWeight: 800,
                fontSize: '1rem', textTransform: 'none',
                boxShadow: tablePickerOpen ? 'none' : `0 6px 20px ${C.green}45`,
                '&:active': { transform: 'scale(0.98)' },
                transition: 'all 0.2s',
              }}
            >
              {placing
                ? 'Sending to kitchen…'
                : tablePickerOpen
                ? 'Pick a table above ↑'
                : `Send to Kitchen — Table ${selectedTable?.tableNumber} 🍳`}
            </Button>
          )}
        </Box>
      </Drawer>

      {/* ── Temp Table Dialog ─────────────────────────────────────────────────── */}
      <Drawer
        anchor="bottom"
        open={tempTableDialogOpen}
        onClose={() => setTempTableDialogOpen(false)}
        PaperProps={{
          sx: {
            background: C.card, borderRadius: '22px 22px 0 0',
            maxWidth: 560, left: '50%', transform: 'translateX(-50%) !important',
            width: '100%', p: 2.5,
            pb: `calc(24px + env(safe-area-inset-bottom, 0px))`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ color: C.text, fontWeight: 800, fontSize: '1rem' }}>
            Create Temporary Table
          </Typography>
          <Box component="button" onClick={() => setTempTableDialogOpen(false)}
            sx={{ border: 'none', background: 'transparent', color: C.muted, cursor: 'pointer', p: 0.5 }}>
            <Close sx={{ fontSize: 20 }} />
          </Box>
        </Box>
        <Typography sx={{ color: C.amber, fontSize: '0.75rem', mb: 2, lineHeight: 1.5 }}>
          This table is for the current bill only and will be removed automatically after payment.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label="Table Number / Name"
            size="small"
            fullWidth
            value={tempTableNumber}
            onChange={e => setTempTableNumber(e.target.value)}
            placeholder="e.g. TEMP-1, Lawn-1, Extra"
            sx={{
              '& .MuiOutlinedInput-root': {
                background: C.bg, borderRadius: '13px', minHeight: 48,
                '& input': { color: C.text }, '& fieldset': { borderColor: C.border },
                '&:hover fieldset': { borderColor: C.amber },
                '&.Mui-focused fieldset': { borderColor: C.amber },
              },
              '& .MuiInputLabel-root': { color: C.muted },
              '& .MuiInputLabel-root.Mui-focused': { color: C.amber },
            }}
          />
          <TextField
            label="Seats / Capacity"
            size="small"
            type="number"
            fullWidth
            value={tempTableCapacity}
            onChange={e => setTempTableCapacity(e.target.value)}
            inputProps={{ min: 1, max: 50 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: C.bg, borderRadius: '13px', minHeight: 48,
                '& input': { color: C.text }, '& fieldset': { borderColor: C.border },
                '&:hover fieldset': { borderColor: C.amber },
                '&.Mui-focused fieldset': { borderColor: C.amber },
              },
              '& .MuiInputLabel-root': { color: C.muted },
              '& .MuiInputLabel-root.Mui-focused': { color: C.amber },
            }}
          />
          <Button
            variant="contained"
            fullWidth
            disabled={!tempTableNumber.trim() || tempTableCreating}
            onClick={createTempTable}
            sx={{
              background: `linear-gradient(135deg, ${C.amber} 0%, #d97706 100%)`,
              minHeight: 52, borderRadius: '14px', fontWeight: 800,
              fontSize: '0.95rem', textTransform: 'none', color: '#fff',
              boxShadow: `0 6px 20px ${C.amber}40`,
              '&:active': { transform: 'scale(0.98)' },
              '&.Mui-disabled': { opacity: 0.5 },
            }}
          >
            {tempTableCreating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Create & Select Table'}
          </Button>
        </Box>
      </Drawer>

      {/* ── Order success overlay ─────────────────────────────────────────────── */}
      {orderPlaced && (
        <Box sx={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 2.5,
          px: 3,
        }}>
          <Box sx={{
            width: 90, height: 90, borderRadius: '24px',
            background: `${C.green}20`, border: `2px solid ${C.green}50`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'ca-success 0.5s ease',
          }}>
            <Check sx={{ fontSize: 50, color: C.green }} />
          </Box>
          <Typography variant="h5" sx={{ color: '#fff', fontWeight: 900, textAlign: 'center' }}>
            Order Sent!
          </Typography>
          <Typography sx={{ color: C.muted, fontSize: '0.9rem', textAlign: 'center' }}>
            Kitchen has been notified 🍳
          </Typography>
        </Box>
      )}
    </>
  );
};

export default CaptainApp;
